import CartButton from '@/components/cart/cart-button';
import { useDrawer } from '@/components/drawer-views/context';
import { UserIcon } from '@/components/icons/user-icon';
import { useModalAction } from '@/components/modal-views/context';
import ProductSearchBar, {
  useSearchText,
} from '@/components/product/product-searchbar';
import Button from '@/components/ui/button-old';
import { Menu } from '@/components/ui/dropdown';
import Hamburger from '@/components/ui/hamburger';
import ActiveLink from '@/components/ui/links/active-link';
import Logo from '@/components/ui/logo';
import ThemeSwitcher from '@/components/ui/theme-switcher';
import { Transition } from '@/components/ui/transition';
import routes from '@/config/routes';
import { useLogout, useMe } from '@/data/user';
import { useIsMounted } from '@/lib/hooks/use-is-mounted';
import { useSwapBodyClassOnScrollDirection } from '@/lib/hooks/use-swap-body-class';
import type { User } from '@/types';
import { cn } from '@nextui-org/react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { Fragment, useRef } from 'react';
import Avatar from 'react-avatar';
import { Nav, NavGenerate } from '@/lib/constants/nav';
import NSFWToggle from '@/components/nsfw/nsfw-toggle';
import CreditsMenu from '@/components/credits/credits-menu';
import { ExternalLinkIcon } from 'lucide-react';
import { FeedIcon } from '@/components/icons/feed-icon';
import WishlistButton from '@/components/wishlist/wishlist-button';
import { Tooltip } from '@nextui-org/react';
import { Product } from '@/types';
import { useProductsPaginated } from '@/data/product';
import SearchButton from '@/components/search/search-button';

interface ExtendedUser extends User {
  products?: Product[];
}

const AuthorizedMenuItems = [
  {
    // TODO: label: 'text-auth-profile',
    label: 'Account',
    path: routes.profile,
  },
  {
    label: 'text-auth-purchase',
    path: routes.purchases,
  },
  {
    label: 'text-auth-wishlist',
    path: routes.wishlists,
  },
  {
    label: 'text-followed-authors',
    path: routes.followedShop,
  },
  // {
  //   label: 'text-auth-password',
  //   path: routes.password,
  // },
  {
    label: 'My AI Gallery',
    path: routes.myAIGallery,
  },
];

function AuthorizedMenu({ user }: { user: ExtendedUser }) {
  const { mutate: logout } = useLogout();
  const { t } = useTranslation('common');
  return (
    <Menu>
      <Menu.Button className="relative inline-flex h-8 w-8 justify-center rounded-full border border-light-400 bg-white dark:border-dark-500 dark:bg-dark-500">
        {/* @ts-ignore */}
        <Avatar
          size="32"
          round={true}
          name={user.name}
          textSizeRatio={2}
          src={user?.profile?.avatar?.thumbnail}
        />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute top-[84%] z-30 mt-4 w-56 rounded-md bg-light py-1.5 text-dark shadow-dropdown ltr:right-0 ltr:origin-top-right rtl:left-0 rtl:origin-top-left dark:bg-[#0B0B0B] dark:text-light">
          {AuthorizedMenuItems.map((item) => (
            <Menu.Item key={item.label}>
              <ActiveLink
                href={item.path}
                className="transition-fill-colors flex w-full items-center px-5 py-2.5 hover:bg-light-400 dark:hover:bg-dark-600"
              >
                {t(item.label)}
              </ActiveLink>
            </Menu.Item>
          ))}
          <div className="my-1 border-t border-light-400 dark:border-dark-400" />
          {/* <Menu.Item>
            <ActiveLink
              href={routes.sellerDashboard}
              className="transition-fill-colors flex w-full items-center px-5 py-2.5 hover:bg-light-400 dark:hover:bg-dark-600"
            >
              Seller Dashboard
              <ExternalLinkIcon className="ml-2 h-4 w-4" />
            </ActiveLink>
          </Menu.Item> */}
          <Menu.Item>
            <button
              type="button"
              className="transition-fill-colors w-full px-5 py-2.5 hover:bg-light-400 ltr:text-left rtl:text-right dark:hover:bg-dark-600"
              onClick={() => logout()}
            >
              {t('text-logout')}
            </button>
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

function LoginMenu() {
  const { openModal } = useModalAction();
  const { me, isAuthorized, isLoading } = useMe();
  const isMounted = useIsMounted();
  if (!isMounted) {
    return (
      <div className="h-8 w-8 animate-pulse rounded-full bg-white dark:bg-dark-500" />
    );
  }
  if (isAuthorized && me && !isLoading) {
    return <AuthorizedMenu user={me} />;
  }
  return (
    <Button
      variant="icon"
      aria-label="User"
      className="flex"
      onClick={() => openModal('REGISTER')}
    >
      <UserIcon className="h-5 w-5" />
    </Button>
  );
}

interface HeaderProps {
  isCollapse?: boolean;
  showHamburger?: boolean;
  onClickHamburger?: () => void;
}

export default function Header({
  isCollapse,
  showHamburger = false,
  onClickHamburger,
}: HeaderProps) {
  const { asPath, pathname, locale } = useRouter();
  const { t } = useTranslation('common');
  useSwapBodyClassOnScrollDirection();
  const isMultiLangEnable =
    process.env.NEXT_PUBLIC_ENABLE_MULTI_LANG === 'true' &&
    !!process.env.NEXT_PUBLIC_AVAILABLE_LANGUAGES;

  const searchRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { openDrawer } = useDrawer();
  const { me, isLoading } = useMe();
  const walletPoints = !me ? 4 : me?.wallet?.available_points ?? 0;
  const walletTrainPoints = me?.wallet?.available_train_model_points || 0;

  const { products } = useProductsPaginated(
    {
      language: locale,
      limit: 10,
      shop_id:
        me?.shops?.length && Boolean(me?.shops[0].id)
          ? me?.shops[0].id.toString()
          : undefined, // TODO: handle this
      is_custom_ai_model: true,
    },
    {
      enabled: me?.shops?.length ? Boolean(me?.shops[0].id) : false,
    }
  );

  const hasCustomModel = products?.length > 0;

  const { setMarketplaceSearch, setHeaderSearch } = useSearchText();

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMarketplaceSearch('');
    setHeaderSearch('');
    router.push('/', undefined, { shallow: true });
  };

  const isGenerateActive = pathname.includes('/my-consistent-ai');
  const isSellerActive = pathname === '/become-seller';
  const isMarketplaceActive = pathname === '/products' || pathname === '/';

  const isFeedActive = pathname === routes.feed;

  return (
    <header
      className="sticky top-0 z-30 w-full bg-background/70 backdrop-blur-lg backdrop-saturate-150 duration-300 transition-transform"
      style={{ transform: 'translateY(0)' }}
    >
      <div className="flex h-16 w-full items-center justify-between px-4 sm:h-[70px] sm:px-6 lg:px-8">
        <div className="flex items-center gap-2">
          <div onClick={handleLogoClick} className="cursor-pointer">
            <Logo className="w-40" />
          </div>
        </div>

        <nav className="mr-auto ml-8 hidden items-center space-x-6 lg:flex 2xl:ml-16">
          {me && me.id && (
            <ActiveLink
              href={routes.feed}
              className={cn(
                'flex items-center px-2 py-2 text-xs font-medium duration-200 transition-all lg:px-3 2xl:text-sm',
                isFeedActive
                  ? 'border-b-3 border-brand text-default-800'
                  : 'rounded-md text-default-600 hover:bg-default-100 hover:text-default-800'
              )}
            >
              {t('text-feed')}
              <FeedIcon className="ml-1 h-2.5 w-2.5 text-current lg:ml-2 lg:h-3 lg:w-3" />
            </ActiveLink>
          )}
          <ActiveLink
            href="/products"
            className={cn(
              'relative px-2 py-2 text-xs font-medium duration-200 transition-all lg:px-3 2xl:text-sm',
              isMarketplaceActive
                ? 'border-b-3 border-brand text-default-800'
                : 'rounded-md text-default-600 hover:bg-default-100 hover:text-default-800'
            )}
          >
            Marketplace
          </ActiveLink>
          {/* <div className="relative">
            <ActiveLink
              href={
                hasCustomModel
                  ? '/my-consistent-ai/overview'
                  : '/my-consistent-ai'
              }
              className={cn(
                'px-2 py-2 text-xs font-medium duration-200 transition-all lg:px-3 2xl:text-sm',
                isGenerateActive
                  ? 'border-b-3 border-brand text-default-800'
                  : 'rounded-md text-default-600 hover:bg-default-100 hover:text-default-800'
              )}
            >
              My Consistent AI
            </ActiveLink>
            <span className="absolute -top-2 -right-4 animate-bounce rounded-md bg-red-600 px-1 py-0.5 text-[8px] font-medium text-white shadow-sm lg:-top-3 lg:-right-5 lg:text-[10px]">
              New
            </span>
          </div> */}
          {false && me && me.shops.length > 0 ? (
            <a
              href="/seller"
              className={cn(
                'px-2 py-2 text-xs font-medium duration-200 transition-all lg:px-3 2xl:text-sm',
                isSellerActive
                  ? 'border-b-3 border-brand text-default-800'
                  : 'rounded-md text-default-600 hover:bg-default-100 hover:text-default-800'
              )}
            >
              <span className="flex items-center">
                Seller Dashboard{' '}
                <ExternalLinkIcon className="ml-1 h-3 w-3 lg:h-4 lg:w-4" />
              </span>
            </a>
          ) : (
            <ActiveLink
              href="/become-seller"
              className={cn(
                'px-2 py-2 text-xs font-medium duration-200 transition-all lg:px-3 2xl:text-sm',
                isSellerActive
                  ? 'border-b-3 border-brand text-default-800'
                  : 'rounded-md text-default-600 hover:bg-default-100 hover:text-default-800'
              )}
            >
              Earn Money
            </ActiveLink>
          )}
        </nav>

        <div className="flex items-center space-x-3 sm:space-x-4">
          <div className="hidden items-center gap-4 xl:flex">
            <ProductSearchBar
              searchRef={searchRef}
              selectedTab={'all'}
              className="!w-[260px] !bg-transparent 2xl:!w-[360px]"
              classNameInputWrapper="bg-default-400/20 !h-9"
              size="sm"
              context="header"
            />
          </div>
          <div className="hidden items-center gap-4 lg:flex xl:hidden">
            <SearchButton />
          </div>
          <NSFWToggle size="md" />
          <Tooltip content={t('Toggle theme')}>
            <div className="hidden min-[355px]:flex">
              <ThemeSwitcher />
            </div>
          </Tooltip>
          <Tooltip content={t('Your watchlist')}>
            <div className="hidden xs:flex">
              <WishlistButton className="text-default-600 hover:text-default-800" />
            </div>
          </Tooltip>
          <Tooltip content={t('Your cart')}>
            <div className="hidden md:flex">
              <CartButton className="text-default-600 hover:text-default-800" />
            </div>
          </Tooltip>
          <LoginMenu />

          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <Hamburger onClick={() => openDrawer('MOBILE_MENU')} />
          </div>
        </div>
      </div>

      {/* Secondary navigation and credits menu */}
      <div className="hidden items-center justify-center lg:flex">
        <nav className="hidden min-h-[45px] w-full border-t border-default-300/70 bg-default-100/10 shadow-[0_1px_3px_0_rgb(0,0,0,0.05)] backdrop-blur-sm md:flex">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto flex w-full justify-start">
              {isGenerateActive
                ? NavGenerate.map((item, index) => (
                    <div key={index} className="group relative">
                      <ActiveLink
                        href={item.href}
                        className={cn(
                          'inline-flex items-center border-b-2 border-transparent px-4 py-2.5 text-medium transition-all',
                          pathname === item.href
                            ? 'border-brand bg-default-200/30 dark:bg-default-800/10'
                            : 'group-hover:border-brand group-hover:bg-default-200/30 dark:group-hover:bg-default-800/20'
                        )}
                      >
                        {item.label}
                      </ActiveLink>
                    </div>
                  ))
                : Nav.sort((a, b) => a.label.localeCompare(b.label)).map(
                    (item, index) => (
                      <div key={index} className="group relative">
                        <ActiveLink
                          href={`/products${item.href}`}
                          className={cn(
                            'inline-flex items-center border-b-2 border-transparent px-4 py-2.5 text-medium transition-all',
                            asPath.includes(item.href.split('?')[1])
                              ? 'border-brand bg-default-200/30 dark:bg-default-800/10'
                              : 'group-hover:border-brand group-hover:bg-default-200/30 dark:group-hover:bg-default-800/20'
                          )}
                        >
                          {item.label}
                        </ActiveLink>
                        <div className="absolute left-0 hidden w-56 rounded-md bg-default-100 shadow-md backdrop-blur-lg group-hover:block">
                          <div className="p-4 text-medium">
                            {item.subItems
                              .sort((a, b) => a.label.localeCompare(b.label))
                              .map((subItem, subIndex) => (
                                <ActiveLink
                                  key={subIndex}
                                  href={`/products${subItem.href}`}
                                  className={cn(
                                    'block rounded-md py-1.5 px-2',
                                    pathname === `/products${subItem.href}`
                                      ? 'bg-default-200/50 dark:bg-dark-900/50'
                                      : 'hover:bg-default-200/50 dark:hover:bg-dark-900/50'
                                  )}
                                >
                                  {subItem.label}
                                </ActiveLink>
                              ))}
                          </div>
                        </div>
                      </div>
                    )
                  )}
            </div>
          </div>
        </nav>
        {/* <CreditsMenu
          walletPoints={isLoading ? '-' : walletPoints}
          walletTrainPoints={isLoading ? '-' : walletTrainPoints}
        /> */}
      </div>
    </header>
  );
}
